import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BASE_API_URL } from '@curbnturf/entities';
import { IAddress, IUser } from '@curbnturf/objects';
import { Observable, map } from 'rxjs';

export interface IGrowClientListing {
  id: number;
  name?: string;
  /** Real backend availability — once true, only the client (or an admin) can edit it. */
  available: boolean;
}

/**
 * A sales rep's entire referral list, regardless of listing count or publish
 * state — the server's findClients() now returns everyone under the rep's
 * affiliateRef in one query (leads/prospects endpoints are deprecated and
 * always return empty; see GrowService). Every row is a real user account —
 * the old bare "lead contact" concept (no account yet) no longer surfaces here.
 */
export interface IGrowClient {
  key: string;
  userId?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  address?: IAddress;
  billing?: IAddress;
  listingCount?: number;
  listings?: IGrowClientListing[];
  created?: number;
}

interface IClientsResponse {
  total: number;
  results: IUser[];
}

/** Large enough to cover a rep's full referral list in one page — this view has no pagination UI. */
const PAGE_SIZE = 1000;

@Injectable({ providedIn: 'root' })
export class GrowClientsService {
  private http = inject(HttpClient);
  private readonly url = `${BASE_API_URL}grow`;

  getClients$(queryTerm?: string): Observable<IGrowClient[]> {
    const params = new HttpParams({
      fromObject: { page: '1', pageSize: String(PAGE_SIZE), ...(queryTerm ? { queryTerm } : {}) },
    });

    return this.http.get<IClientsResponse>(`${this.url}/clients`, { params }).pipe(
      map((clientsRes) => {
        const fromClients: IGrowClient[] = clientsRes.results.map((user) => ({
          key: `user-${user.id}`,
          userId: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone,
          address: user.address,
          billing: user.billing,
          listingCount: user.listings?.length ?? 0,
          listings: (user.listings ?? [])
            .filter((l) => l.id != null)
            .map((l) => ({ id: l.id as number, name: l.name, available: !!l.available })),
          created: user.created,
        }));
        return fromClients.sort((a, b) => (b.created ?? 0) - (a.created ?? 0));
      }),
    );
  }

  /**
   * Creates a real backend account (Cognito + IUser row) for a referral,
   * scoped to the current rep's affiliateRef — used so "Add contact" produces
   * an actual client instead of a bare, account-less lead record. Fails with
   * a 400 if the email is already registered under a different rep.
   */
  registerClient(payload: Partial<IUser>): Observable<IUser> {
    return this.http.post<IUser>(`${this.url}/register-client`, payload);
  }

  /**
   * Edits an arbitrary client's profile by id — the same generic `PUT user/:id`
   * endpoint any user uses to edit their own profile (no Grow-specific route
   * exists). The server's `verifyUserOwnership` already permits this for a
   * sales/salesManager rep when `user.source === affiliateRef`.
   */
  updateClient(userId: number, patch: Partial<IUser>): Observable<IUser> {
    return this.http.put<IUser>(`${BASE_API_URL}user/${userId}`, { ...patch, id: userId });
  }

  /** Forces a fresh temp password and emails the client their login credentials. No-op (400) if already sent. */
  sendWelcomeEmail(userId: number): Observable<boolean> {
    return this.http.post<boolean>(`${this.url}/${userId}/send-welcome-email`, {});
  }

  /**
   * Notifies the client that a rep-built listing is ready for them to review
   * and publish. Legacy names the route param `siteId` but the server treats
   * it as the listing id.
   */
  notifyClientListingReady(listingId: number): Observable<unknown> {
    return this.http.get(`${this.url}/client/email-listing-notify/${listingId}`);
  }

  /**
   * Notifies the client that a rep started a listing on their behalf and
   * handed it off unfinished — distinct from `notifyClientListingReady`,
   * which implies the listing is complete and just needs a publish.
   */
  notifyClientListingHandoff(listingId: number): Observable<unknown> {
    return this.http.get(`${this.url}/client/email-listing-handoff/${listingId}`);
  }

  /**
   * Opts a new client into the marketing newsletter — mirrors
   * `UserMarketingService.registerForNewsletter()` (@curbnturf/user) without
   * pulling in that lib's NgRx/CachedHttpClient dependency chain. No user id
   * needed; it's a plain marketing-list signup by email.
   */
  registerForNewsletter(email: string, firstName?: string, lastName?: string): Observable<unknown> {
    return this.http.post(`${BASE_API_URL}notification-settings/newsletter`, { email, firstName, lastName });
  }
}
